/**
 * @file mlrIdentityFields.ts
 * @brief Editable §1 identity table values for the WEBPANEL MLR editor.
 *
 * @details
 * Every identity cell is typed on the paper. Values persist as Case Hub
 * `identitySheet` plus claimant / refs / schedule overlays. Live §2 copy
 * reads `caseHubFromIdentity` so a name change shows immediately
 * (08-09-2026).
 *
 * @references
 * - BACKEND/src/features/medical-assessment/identitySheet.mjs
 *
 * @author DES
 * @date 08-09-2026
 */

import type { CaseHub } from "./schemas";

/**
 * Cover-sheet name and UK short date. They moved here on 13-09-2026 when
 * `mlrEditorCopy.ts` was retired: both read the identity sheet, and the
 * section model now supplies everything else that file used to hold.
 */
export function formatClaimantNameFromHub(caseHub: CaseHub | undefined): string {
  const claimant = caseHub?.claimant ?? {};
  return [claimant.title, claimant.forenames, claimant.surname]
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function formatMlrShortDate(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return toUkSlash(value);
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : toUkSlash(parsed);
  }
  const dmy = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) {
    const day = dmy[1].padStart(2, "0");
    const month = dmy[2].padStart(2, "0");
    return `${day}/${month}/${dmy[3]}`;
  }
  return raw;
}

function toUkSlash(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
}

export const MLR_IDENTITY_LABEL = {
  fullName: "Claimant's full name",
  caseNumber: "Case number",
  address: "Address",
  occupation: "Occupation",
  dateOfBirth: "Date of Birth",
  idProvided: "ID provided",
  dateOfIncident: "Date of incident",
  ageAtIncident: "Age of the claimant at time of incident",
  examDate: "Date of examination (Length)",
  examPlace: "Place of examination",
  timeSince: "Time since incident",
  accompaniedBy: "Accompanied by",
  instructingParty: "Name of instructing party",
  instructingRef: "Instructing party ref",
  agency: "Agency",
  agencyRef: "Agency ref",
  medcoRef: "MedCo ref",
  reportDate: "Date of report",
} as const;

function parseUkDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const dmy = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) {
    return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  }
  return null;
}

function calculateAgeYears(
  dob: string | null | undefined,
  atDate: string | null | undefined,
): number | null {
  const birth = parseUkDate(dob ?? null);
  const at = parseUkDate(atDate ?? null);
  if (!birth || !at) return null;
  let age = at.getFullYear() - birth.getFullYear();
  const monthDiff = at.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && at.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

function formatElapsedSinceIncident(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string | null {
  const start = parseUkDate(startDate ?? null);
  const end = parseUkDate(endDate ?? null);
  if (!start || !end || end < start) return null;
  let months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  let days = end.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
    days += prevMonth.getDate();
  }
  const monthPart =
    months > 0 ? `${months} month${months === 1 ? "" : "s"}` : "";
  const dayPart = days > 0 ? `${days} day${days === 1 ? "" : "s"}` : "";
  if (monthPart && dayPart) return `${monthPart} ${dayPart}`;
  return monthPart || dayPart || "0 days";
}

function examDateOnly(value: string): string {
  return value.replace(/\s*\(\d+\s*minutes?\)\s*$/i, "").trim();
}

export function patchIdentityValue(
  current: Record<string, string>,
  label: string,
  value: string,
): Record<string, string> {
  const next = { ...current, [label]: value };
  if (
    label === MLR_IDENTITY_LABEL.dateOfBirth ||
    label === MLR_IDENTITY_LABEL.dateOfIncident
  ) {
    const age = calculateAgeYears(
      next[MLR_IDENTITY_LABEL.dateOfBirth],
      next[MLR_IDENTITY_LABEL.dateOfIncident],
    );
    next[MLR_IDENTITY_LABEL.ageAtIncident] =
      age != null ? `${age} Years` : "";
  }
  if (
    label === MLR_IDENTITY_LABEL.dateOfIncident ||
    label === MLR_IDENTITY_LABEL.examDate
  ) {
    const elapsed = formatElapsedSinceIncident(
      next[MLR_IDENTITY_LABEL.dateOfIncident],
      examDateOnly(next[MLR_IDENTITY_LABEL.examDate] ?? ""),
    );
    next[MLR_IDENTITY_LABEL.timeSince] = elapsed ?? "";
  }
  return next;
}

